// /app/frontend/src/components/chat/SuggestedStarters.jsx
import React, { useEffect, useRef, useState } from "react";

const DAY_MS = 24 * 60 * 60 * 1000;

function safeNum(v, fallback = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
}

function getStarterKey(userId, matchId) {
  return `starter:${userId || "anon"}:${matchId || "nomatch"}:lastShown`;
}

// Fixed starter questions
const BASE_STARTERS = [
  "What brought you to Unity Love — and what kind of connection are you hoping for?",
  "How do you describe your relationship style in real life (not the label, the lived experience)?",
  "What's something you value most right now: honesty, peace, growth, adventure — and why?",
];

// Custom hook to check eligibility (runs once on mount)
function useStarterEligibility(userId, matchId, messagesLen, minThreshold) {
  const [eligible, setEligible] = useState(false);
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    if (!userId || !matchId) {
      setEligible(false);
      return;
    }
    if (typeof messagesLen !== "number" || messagesLen >= minThreshold) {
      setEligible(false);
      return;
    }

    const key = getStarterKey(userId, matchId);
    const lastShown = safeNum(localStorage.getItem(key));
    const now = Date.now();
    setEligible(now - lastShown >= DAY_MS);
  }, [userId, matchId, messagesLen, minThreshold]);

  return eligible;
}

export default function SuggestedStarters({
  userId,
  matchId,
  messagesLen,
  matchProfile,
  onInsert,
  trackEvent,
  minThreshold = 4,
}) {
  const didTrackRef = useRef(false);
  const eligible = useStarterEligibility(userId, matchId, messagesLen, minThreshold);

  // Build starters with optional personalization
  const starters = React.useMemo(() => {
    const result = [...BASE_STARTERS];
    
    // Light personalization if background is available
    const bg = (matchProfile?.background || "").toString().trim();
    if (bg && bg.length <= 80) {
      result[0] = `I saw you mentioned "${bg}". What's the story behind that?`;
    }
    
    return result;
  }, [matchProfile]);

  // Track exposure once
  useEffect(() => {
    if (!eligible) return;
    if (didTrackRef.current) return;
    didTrackRef.current = true;

    const key = getStarterKey(userId, matchId);
    localStorage.setItem(key, String(Date.now()));

    trackEvent?.("starter_shown", {
      match_id: matchId,
      messages_len: messagesLen,
    });
  }, [eligible, userId, matchId, messagesLen, trackEvent]);

  if (!eligible) return null;

  return (
    <div
      className="mt-3 rounded-2xl border border-gray-200 dark:border-slate-700 surface-card p-3 shadow-sm"
      data-testid="starter-card"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900 dark:text-gray-100">Start strong</div>
          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Tap to insert (editable). Nothing auto-sends.
          </div>
        </div>
      </div>

      <div className="mt-3 grid gap-2">
        {starters.map((s, idx) => (
          <button
            key={idx}
            type="button"
            className="rounded-xl bg-gray-50 dark:bg-slate-800 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition"
            data-testid={`starter-option-${idx}`}
            onClick={() => {
              onInsert?.(s);
              trackEvent?.("starter_used", {
                match_id: matchId,
                messages_len: messagesLen,
                starter_index: idx,
              });
            }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
